import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminApi, type AdminStats, type DashboardMetrics } from "../api/admin";

const KpiCard = ({
  label,
  value,
  accent,
  hint,
}: {
  label: string;
  value: number | string;
  accent?: string;
  hint?: string;
}) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`mt-1 text-2xl font-semibold ${accent ?? "text-slate-900"}`}>{value}</div>
    {hint && <div className="mt-0.5 text-xs text-slate-500">{hint}</div>}
  </div>
);

// Простой SVG line chart -- спека (docs/spec/Админ/Описание страницы
// дашборда) явно требует линейные графики для месячных уникальных
// пользователей и средней нагрузки по часам. Используем тот же интерфейс,
// что и у Bars, чтобы место подмены оставалось точечным.
const Line = ({
  data,
  labelKey,
  valueKey,
  height = 140,
  formatLabel,
}: {
  data: any[];
  labelKey: string;
  valueKey: string;
  height?: number;
  formatLabel?: (v: any) => string;
}) => {
  if (!data || data.length === 0) {
    return <div className="text-xs text-slate-400">Нет данных</div>;
  }
  const max = Math.max(...data.map((d) => Number(d[valueKey]) || 0), 1);
  const width = 1000;
  const step = data.length > 1 ? width / (data.length - 1) : 0;
  const topPad = 8;
  const usable = height - topPad;
  const points = data.map((d, i) => {
    const x = data.length === 1 ? width / 2 : i * step;
    const v = Number(d[valueKey]) || 0;
    const y = topPad + (1 - v / max) * usable;
    return { x, y, v, label: d[labelKey] };
  });
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaPath = `${path} L ${points[points.length - 1].x} ${height} L ${points[0].x} ${height} Z`;
  return (
    <svg viewBox={`0 0 ${width} ${height + 24}`} className="w-full">
      <path d={areaPath} fill="#0ea5a4" opacity={0.12} />
      <path d={path} fill="none" stroke="#0ea5a4" strokeWidth={3} strokeLinejoin="round" />
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={3} fill="#0ea5a4" />
      ))}
      {points.map(
        (p, i) =>
          (data.length <= 14 || i % Math.ceil(data.length / 10) === 0) && (
            <text
              key={`l-${i}`}
              x={p.x}
              y={height + 16}
              fontSize={10}
              fill="#64748b"
              textAnchor="middle"
            >
              {formatLabel ? formatLabel(p.label) : String(p.label)}
            </text>
          ),
      )}
    </svg>
  );
};


const Bars = ({
  data,
  labelKey,
  valueKey,
  height = 120,
  formatLabel,
}: {
  data: any[];
  labelKey: string;
  valueKey: string;
  height?: number;
  formatLabel?: (v: any) => string;
}) => {
  if (!data || data.length === 0) {
    return <div className="text-xs text-slate-400">Нет данных</div>;
  }
  const max = Math.max(...data.map((d) => Number(d[valueKey]) || 0), 1);
  const width = 1000;
  const barW = width / data.length;
  return (
    <svg viewBox={`0 0 ${width} ${height + 24}`} className="w-full">
      {data.map((d, i) => {
        const v = Number(d[valueKey]) || 0;
        const h = (v / max) * height;
        return (
          <g key={i} transform={`translate(${i * barW}, 0)`}>
            <rect
              x={2}
              y={height - h}
              width={barW - 4}
              height={h}
              fill="#0ea5a4"
              opacity={v ? 0.85 : 0.2}
            />
            {i % Math.ceil(data.length / 10) === 0 && (
              <text
                x={barW / 2}
                y={height + 14}
                fontSize={10}
                fill="#64748b"
                textAnchor="middle"
              >
                {formatLabel ? formatLabel(d[labelKey]) : d[labelKey]}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

export const DashboardPage = () => {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchAll = () => {
      Promise.all([
        AdminApi.getStats().catch(() => null),
        AdminApi.getDashboardMetrics().catch(() => null),
      ]).then(([s, m]) => {
        if (cancelled) return;
        setStats(s?.data ?? null);
        setMetrics(m?.data ?? null);
        if (!s && !m) setError("Не удалось загрузить данные");
      });
    };
    fetchAll();
    // active_now is a real-time metric backed by Redis TTL keys --
    // refresh every 30s so the KPI does not go stale on a long-lived
    // dashboard tab.
    const id = setInterval(fetchAll, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return (
    <div className="p-8">
      <h1 className="mb-1 text-2xl font-semibold text-slate-900">Дашборд</h1>
      <p className="mb-6 text-sm text-slate-500">
        Активность пользователей и моделирование нагрузки на платформу.
      </p>

      {error && <div className="mb-4 text-red-600">{error}</div>}

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Активных сессий сейчас" value={metrics?.active_now ?? "—"} accent="text-emerald-700" />
        <KpiCard label="Пик одновременных" value={metrics?.peak ?? "—"} hint="за всё время" />
        <KpiCard
          label="Среднее польз./день"
          value={metrics?.avg_daily_users_30d ?? "—"}
          hint="за последние 30 дней"
        />
        <KpiCard
          label="Всего пользователей"
          value={stats?.total_users ?? "—"}
          hint={`поставщиков: ${stats?.total_suppliers ?? "—"}, на модерации: ${stats?.pending_suppliers ?? "—"}`}
        />
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-2 text-sm font-medium text-slate-700">
            Уникальные пользователи в день (30 дней)
          </h3>
          <Bars
            data={metrics?.daily_unique_30d ?? []}
            labelKey="date"
            valueKey="users"
            formatLabel={(v) => String(v).slice(5)}
          />
        </section>
        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-2 text-sm font-medium text-slate-700">
            Уникальные пользователи в месяц (12 мес)
          </h3>
          <Line data={metrics?.monthly_unique_12m ?? []} labelKey="month" valueKey="users" />
        </section>
      </div>

      <section className="mb-8 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-2 text-sm font-medium text-slate-700">
          Средняя нагрузка по часам (за 30 дней)
        </h3>
        <Line
          data={metrics?.hour_load_avg_30d ?? []}
          labelKey="hour"
          valueKey="avg_requests"
          formatLabel={(v) => `${v}:00`}
        />
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="mb-3 font-medium text-slate-900">Быстрые действия</h2>
        <div className="flex flex-wrap gap-2">
          <Link to="/users" className="rounded-md bg-slate-100 px-3 py-2 text-sm hover:bg-slate-200">
            Все пользователи
          </Link>
          <Link
            to="/users?role=ORGANIZER"
            className="rounded-md bg-slate-100 px-3 py-2 text-sm hover:bg-slate-200"
          >
            Организаторы
          </Link>
          <Link
            to="/users?role=SUPPLIER"
            className="rounded-md bg-slate-100 px-3 py-2 text-sm hover:bg-slate-200"
          >
            Поставщики
          </Link>
          <Link
            to="/verification/organizers"
            className="rounded-md bg-brand px-3 py-2 text-sm text-white"
          >
            Заявки организаторов
          </Link>
          <Link
            to="/verification/suppliers"
            className="rounded-md bg-brand px-3 py-2 text-sm text-white"
          >
            Заявки поставщиков
          </Link>
          <Link to="/moderators" className="rounded-md bg-slate-900 px-3 py-2 text-sm text-white">
            Модераторы
          </Link>
        </div>
      </section>
    </div>
  );
};
